import Vue from 'vue'
import Plyr from 'plyr'
import { partition, shuffle, take, truncate } from 'lodash'
import EventBus from '../_event-bus'
import RequestHelper from '../_request-helper'
import Slider from './slider'
import { $, sleep, toSeconds } from '../_utils'

const MAX_WRONG_ANSWERS = 2
const MODAL_ANIMATION_THRESHOLD = 300
const PASS_MARK = {
  continuous: 60,
  product: 100,
}
const PLYR_CONFIG = {
  clickToPlay: false,
  controls: ['fullscreen', 'play-large'],
}
const QUESTION_COUNT = 5
const QUIZ_FINISH_ENDPOINT = process.env.QUIZ_FINISH_ENDPOINT
const QUIZ_EDU_FINISH_ENDPOINT = process.env.QUIZ_EDU_FINISH_ENDPOINT
const QUIZ_SAVE_ENDPOINT = process.env.QUIZ_SAVE_ENDPOINT
const VIDEO_PLAYBACK_INTERVAL = 100
const VIDEO_PROGRESS_INTERVAL = 200
const WAIT_THRESHOLD = 100

let pauseEventListenerSet = false

Vue.component('vue-quiz-product', {
  template: '#vue-quiz-product-template',
  props: {
    id: { type: String, required: true },
    productType: { type: String, required: true },
    compleated: { type: Number, required: true },
    slidesProduct: { type: Array, required: true },
    slidesEdu: { type: Array, required: true },
    eduType: { type: String, required: true },
    eduStatus: { type: Boolean, default: false },
    slideNumber: { type: Number, default: 0 },
    questions: { type: Array, required: true },
    eduQuestion: { type: Array, required: true },
    afloPoints: { type: Number, default: 0 },
    certPoints: { type: Number, default: 0 },
    eduPoints: { type: Number, default: 0 },
    certUrl: { type: String, default: '' },
    ctaUrl: { type: String, required: true },
    ctaText: { type: String, required: true },
  },
  data() {
    return {
      currentModal: {},
      currentSlide: {},
      finalQuestionIndex: -1,
      finalQuestions: [],
      isFetching: false,
      isModalActive: false,
      isOverlayVisible: false,
      overlayType: '',
      presentationIndex: 0,
      progress: 0,
      quickQuestionIndex: -1,
      quickQuestions: [],
      returnToFullscreen: false,
      shuffledFinalQuestions: [],
      summary: '',
      supposedCurrentTime: 0,
      video: null,
      videoPlaybackInterval: null,
      videoProgressInterval: null,
      activeTab: 'edu',
      sliderKey: 1,
      showProductModal: false,
      showSlides: true,
      showVideoOverlay: true,
    }
  },
  computed: {
    slides,
    type,
  },
  methods: {
    changeTab,
    calculateScore,
    closeQuickQuestion,
    formatQuestions,
    formatSlides,
    handleOverlayPrimaryEvent,
    handleVideoEnd,
    handleEduVideoEnd,
    handleVideoPlayback,
    handleVideoProgress,
    hideModal,
    hideOverlay,
    nextQuestion,
    nextQuickQuestion,
    nextSlide,
    pauseVideo,
    playVideo,
    prevQuestion,
    prevSlide,
    recordAnswer,
    replaySlides,
    replayVideo,
    restartPresentation,
    rewindPresentation,
    setProgressTransitionDuration,
    setQuestion,
    setQuizHeight,
    showError,
    showFailure,
    showModal,
    showSuccess,
    showSummary,
    showOverlay,
    shuffleQuestions,
    startPresentation,
    startSlides,
    startTest,
    startVideo,
    updateProgress,
    validateQuickQuestion,
    startEduTest,
    formatEduQuestions,
    nextEduSlide,
    endEdu,
  },
  created() {
    this.formatQuestions(this.eduQuestion)
    this.formatSlides()

    if ((this.eduStatus && !this.compleated) || this.eduType === 'disabled') {
      if (this.eduType === 'disabled' && !this.eduStatus) {
        try {
          this.eduStatus = 1
          RequestHelper.patch(QUIZ_EDU_FINISH_ENDPOINT.replace('{course}', this.id), {
            finished: true,
          })
        } catch (error) {
          this.eduStatus = 0
          // eslint-disable-next-line no-console
          console.error(`error =>`, error)
        }
      }

      this.changeTab('product')
      if (this.productType === 'product' && this.slideNumber > 0) {
        this.showOverlay('test')
      }
    } else {
      this.startPresentation()
    }
  },
  async mounted() {
    await sleep(WAIT_THRESHOLD)
    window.dispatchEvent(new Event('resize'))
    this.setQuizHeight()
    EventBus.$on('resize', this.setQuizHeight)
  },
})

function calculateScore() {
  const isQuestionCorrect = ({ correctAnswer, selected }) => correctAnswer === selected
  const correctQuestions = this.shuffledFinalQuestions.filter(isQuestionCorrect)

  return (100 * correctQuestions.length) / this.shuffledFinalQuestions.length
}

async function closeQuickQuestion(isValid) {
  this.hideModal()
  this.hideOverlay()
  if (!isValid) return
  await sleep(MODAL_ANIMATION_THRESHOLD)
  this.currentModal.answers = []
  await sleep(0)
  this.nextQuickQuestion()
  if (this.type === 'product') {
    if (this.returnToFullscreen) this.video.fullscreen.enter()
    this.video.play()
  }
}

function formatQuestions(formatTab) {
  const addType = type => question => ({ ...question, type })
  const addId = prefix => (question, index) => ({ ...question, id: `${prefix}${index}` })
  const addValidityFields = question => ({ ...question, invalid: [], valid: [] })
  const formatTime = question => ({ ...question, time: toSeconds(question.time) })
  const isQuickQuestion = question => !!question.time
  const [quickQuestions, finalQuestions] = partition(formatTab, isQuickQuestion)

  this.quickQuestions = []
  this.quickQuestions = quickQuestions
    .map(addId('quick'))
    .map(addType('quickQuestion'))
    .map(addValidityFields)
    .map(formatTime)
  this.finalQuestions = finalQuestions.map(addId('')).map(addType('question'))
}

function formatEduQuestions() {
  this.quickQuestions = []
  this.quickQuestions.push({
    ...this.eduQuestion[0],
    time: this.eduType === 'continuous' ? this.slidesEdu.length : null,
    type: 'quickQuestion',
    invalid: [],
    valid: [],
    isFinal: true,
  })
}

function formatSlides() {
  if (this.type === 'product') {
    this.video = this.slides[0]
    this.currentSlide = {
      type: 'video',
      video: this.video,
    }
  } else if (this.type === 'continuous') {
    this.currentSlide = {
      type: 'images',
      images: this.slides,
    }
  }
}

function handleOverlayPrimaryEvent() {
  switch (this.overlayType) {
    case 'test':
      if (this.finalQuestionIndex === -1) this.startTest()
      else this.showModal()
      break
    default:
      this.showModal()
      break
  }
}

function setQuizHeight() {
  this.$refs.quiz.style.setProperty('--quiz-width', `${this.$refs.quiz.clientWidth}px`)
}

async function handleVideoEnd() {
  if (document.fullscreenElement) this.video.fullscreen.exit()
  this.updateProgress('99%')
  if (this.showVideoOverlay) this.showOverlay('test')
  this.supposedCurrentTime = 0
  if (this.slideNumber === 0 && this.activeTab === 'product') {
    try {
      await RequestHelper.post(QUIZ_SAVE_ENDPOINT.replace('{course}', this.id), {
        stopMoment: 1,
      }).then(() => this.slideNumber++)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`error =>`, error)
    }
  }
}

async function handleEduVideoEnd() {
  if (document.fullscreenElement) this.video.fullscreen.exit()
  this.updateProgress('99%')
  this.showOverlay('quickQuestion')
  this.currentSlide = this.quickQuestions.find(el => el.isFinal)
  this.currentModal = this.quickQuestions.find(el => el.isFinal)
  this.showModal()
  this.quickQuestionIndex++
  this.supposedCurrentTime = 0

  try {
    // await RequestHelper.patch(QUIZ_EDU_FINISH_ENDPOINT.replace('{course}', this.id), {
    //   finished: true,
    // })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`error =>`, error)
  }
}

async function handleVideoPlayback() {
  const { time = Infinity } = this.currentModal
  if (this.video.currentTime >= time) {
    this.video.pause()
    this.showModal()
    this.showOverlay('quickQuestion')
    if (this.video.fullscreen.active) {
      this.video.fullscreen.exit()
      await sleep(WAIT_THRESHOLD * 10)
      this.returnToFullscreen = true
    }
  }
}

function handleVideoProgress() {
  this.updateProgress(`${Math.ceil((99 * this.video.currentTime) / this.video.duration)}%`)
}

function hideModal() {
  this.isModalActive = false
}

function hideOverlay() {
  this.isOverlayVisible = false
}

function nextQuestion() {
  if (this.finalQuestionIndex + 1 >= this.shuffledFinalQuestions.length) {
    this.overlayType = 'summary'
    this.showSummary()
    return
  }
  this.finalQuestionIndex++
  this.setQuestion()
}

function nextQuickQuestion() {
  if (
    this.quickQuestions[this.quickQuestionIndex + 1] &&
    !this.quickQuestions[this.quickQuestionIndex + 1].time
  ) {
    this.quickQuestionIndex++
    this.currentModal = {}
    this.nextQuickQuestion()
  }
  this.currentModal = { ...(this.quickQuestions[++this.quickQuestionIndex] || {}) }
}

async function nextSlide() {
  const newIndex = this.presentationIndex + 1
  this.presentationIndex = Math.min(newIndex, this.slides.length - 1)
  const index =
    this.presentationIndex >= this.slides.length
      ? this.presentationIndex - 1
      : this.presentationIndex
  this.updateProgress(`${Math.ceil((99 * (index + 1)) / this.slides.length)}%`)

  if (this.presentationIndex === this.currentModal.time) {
    this.showModal()
    this.showOverlay('quickQuestion')
  }
  if (this.presentationIndex > this.slideNumber && this.activeTab === 'product') {
    try {
      await RequestHelper.post(QUIZ_SAVE_ENDPOINT.replace('{course}', this.id), {
        stopMoment: this.presentationIndex,
      }).then(() => this.slideNumber++)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`error =>`, error)
    }
  }
}

async function nextEduSlide() {
  const newIndex = this.presentationIndex + 1
  this.presentationIndex = newIndex
  this.updateProgress(`99%`)

  if (this.presentationIndex === this.currentModal.time) {
    this.showModal()
    this.showOverlay('quickQuestion')
  }
  if (this.presentationIndex > this.slideNumber) {
    try {
      // await RequestHelper.patch(QUIZ_EDU_FINISH_ENDPOINT.replace('{course}', this.id), {
      //   finished: true,
      // })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`error =>`, error)
    }
  }
}

function pauseVideo() {
  window.clearInterval(this.videoPlaybackInterval)
  window.clearInterval(this.videoProgressInterval)
}

function playVideo() {
  this.videoPlaybackInterval = window.setInterval(this.handleVideoPlayback, VIDEO_PLAYBACK_INTERVAL)
  this.videoProgressInterval = window.setInterval(this.handleVideoProgress, VIDEO_PROGRESS_INTERVAL)
  this.showVideoOverlay = true
}

function prevQuestion() {
  if (this.finalQuestionIndex === -1) return
  this.finalQuestionIndex--
  this.setQuestion()
}

function prevSlide() {
  const newIndex = this.presentationIndex - 1

  this.presentationIndex = Math.max(0, newIndex)
  const index =
    this.presentationIndex >= this.slides.length
      ? this.presentationIndex - 1
      : this.presentationIndex
  this.updateProgress(`${Math.ceil((99 * (index + 1)) / this.slides.length)}%`)
}

function recordAnswer(answerIndex) {
  this.currentModal.selected = answerIndex
}

function replaySlides() {
  this.presentationIndex = 0
  this.slider.slideTo(this.presentationIndex)
  const index =
    this.presentationIndex >= this.slides.length
      ? this.presentationIndex - 1
      : this.presentationIndex
  this.updateProgress(`${Math.ceil((99 * (index + 1)) / this.slides.length)}%`)
}

function replayVideo() {
  this.updateProgress('0%')
  this.video.currentTime = 0
  if (this.activeTab === 'edu' && !this.quickQuestions.some(el => el.isFinal)) {
    // this.quickQuestions.push({
    //   ...this.eduQuestion[0],
    //   time: this.video.duration - 0.1,
    //   type: 'quickQuestion',
    //   invalid: [],
    //   valid: [],
    //   isFinal: true
    // })
    this.quickQuestionIndex = -1
    this.nextQuickQuestion()
  }
  this.video.play()
}

function restartPresentation() {
  this.hideModal()
  this.hideOverlay()
  this.quickQuestionIndex = -1
  this.finalQuestionIndex = -1
  this.nextQuickQuestion()
  if (this.type === 'product') this.replayVideo()
  else if (this.type === 'continuous') this.replaySlides()
}

function rewindPresentation() {
  this.hideModal()
  this.hideOverlay()
  if (this.type === 'product') {
    if (this.returnToFullscreen) this.video.fullscreen.enter()
    this.video.currentTime = this.quickQuestions[this.quickQuestionIndex - 1]?.time || 0
    this.video.play()
  } else if (this.type === 'continuous') {
    const newIndex = this.quickQuestions[this.quickQuestionIndex - 1]?.time || 0
    this.slider.slideTo(newIndex)
    this.updateProgress(`${Math.ceil((99 * (newIndex + 1)) / this.slides.length)}%`)
    this.presentationIndex = newIndex
  }
}

function setProgressTransitionDuration() {
  this.$refs.progress.style.setProperty(
    '--progress-transition-duration',
    `${(VIDEO_PROGRESS_INTERVAL * 0.9) / 1000}s`
  )
}

async function setQuestion() {
  const answers = this.currentModal.answers
  this.currentModal.answers = []
  await sleep(0)
  this.currentModal.answers = answers
  this.currentModal = this.shuffledFinalQuestions[this.finalQuestionIndex]
  await sleep(0)
  const progress = `${((this.finalQuestionIndex + 1) / this.shuffledFinalQuestions.length) * 100}%`
  this.$refs.testProgress.style.setProperty('--test-progress', progress)
}

function showError() {
  this.currentModal = { type: 'error' }
  this.showModal()
  this.showOverlay('error')
}

function showFailure() {
  this.currentModal = { type: 'failure' }
  this.showModal()
  this.showOverlay('failure')
}

function showModal() {
  this.isModalActive = true
}

async function showSuccess() {
  this.currentModal = { type: 'success' }
  this.showModal()
  this.showOverlay('success')
  await sleep(0)
  this.updateProgress('100%')
}

async function showSummary() {
  const score = this.calculateScore()
  const isPassed = score >= PASS_MARK[this.type]

  if (isPassed && this.activeTab === 'product') {
    this.isFetching = true
    try {
      const { data } = await RequestHelper.post(QUIZ_FINISH_ENDPOINT.replace('{course}', this.id), {
        finished: true,
      })
      const { message, status } = data
      if (status !== 'success') throw Error
      this.compleated = 1
      this.summary = message
      this.showSuccess()
    } catch (error) {
      this.showError()
      // eslint-disable-next-line no-console
      console.error(`error =>`, error)
    }
    this.isFetching = false
  } else if (isPassed && this.activeTab === 'edu') {
    this.isFetching = true
    try {
      const { data } = await RequestHelper.patch(
        QUIZ_EDU_FINISH_ENDPOINT.replace('{course}', this.id),
        {
          finished: true,
        }
      )
      const { status } = data
      if (status !== 'success') throw Error
      this.endEdu()
    } catch (error) {
      this.showError()
      // eslint-disable-next-line no-console
      console.error(`error =>`, error)
    }
    this.isFetching = false
  } else {
    this.showFailure()
  }
}

function endEdu() {
  this.summary = `Udało Ci się ukończyć szkolenie.`
  this.eduStatus = 1
  this.showSuccess()
}

function showOverlay(type) {
  if (type) this.overlayType = type
  this.isOverlayVisible = true
}

function shuffleQuestions() {
  const questionCount = QUESTION_COUNT * (this.certPoints || 1)
  const shuffledFinalQuestions = take(shuffle(this.finalQuestions), questionCount)
  const addSelected = question => ({ ...question, selected: null })

  this.shuffledFinalQuestions = shuffledFinalQuestions.map(addSelected)
}

async function startPresentation() {
  await sleep(0)
  this.setProgressTransitionDuration()
  this.quickQuestionIndex = -1
  this.nextQuickQuestion()
  if (this.type === 'continuous') this.startSlides()
  else if (this.type === 'product') {
    this.startVideo()
  }
}

function startSlides() {
  this.presentationIndex = this.slideNumber
  this.slider = new Slider(this.$refs.slider)
  this.slider.init(this.presentationIndex)
  const index =
    this.presentationIndex >= this.slides.length
      ? this.presentationIndex - 1
      : this.presentationIndex
  this.updateProgress(`${Math.ceil((99 * (index + 1)) / this.slides.length)}%`)
  this.slider.on('slideNextTransitionStart', this.nextSlide)
  this.slider.on('slidePrevTransitionStart', this.prevSlide)

  if (
    (this.activeTab === 'edu' && !this.eduStatus) ||
    (this.activeTab === 'product' && !this.compleated)
  ) {
    this.presentationIndex = 0
    this.slider.slideTo(this.presentationIndex)
    const index =
      this.presentationIndex >= this.slides.length
        ? this.presentationIndex - 1
        : this.presentationIndex
    if (this.slideNumber === this.slides.length - 1) {
      this.updateProgress('99%')
    } else {
      this.updateProgress(`${Math.ceil((99 * (index + 1)) / this.slides.length)}%`)
    }
  } else {
    this.showOverlay('test')
    this.updateProgress('99%')
  }
}

function startTest() {
  this.finalQuestionIndex = -1
  this.shuffleQuestions()
  this.hideOverlay()
  this.showModal()
  this.nextQuestion()
}

function startVideo() {
  this.video = new Plyr(this.$refs.video, PLYR_CONFIG)
  this.video.on('play', this.playVideo)
  if (this.activeTab !== 'edu') this.video.on('ended', this.handleVideoEnd)
  else this.video.on('ended', this.handleVideoEnd)
  this.video.on('pause', this.pauseVideo)
  this.video.on('exitfullscreen', () => {
    this.returnToFullscreen = false
  })
  this.video.on('timeupdate', () => {
    if (this.video.seeking) return
    this.supposedCurrentTime = this.video.currentTime
  })
  this.video.on('seeking', () => {
    const delta = this.video.currentTime - this.supposedCurrentTime
    if (delta <= 0.01) return
    this.video.currentTime = this.supposedCurrentTime
  })

  /**
   * Plyr does not support tapping to pause the video. Tapping during playback is
   * used to show the video controls.
   * Therefore this "feature" needs to be added on top of Plyr.
   */
  if (!pauseEventListenerSet) {
    pauseEventListenerSet = true
    this.$refs.quiz.addEventListener('click', event => {
      if (!event.target.classList.contains('plyr__poster')) return
      this.video.togglePlay()
    })
  }

  if (this.slideNumber === 0 && !(this.activeTab === 'edu' && this.eduStatus))
    this.updateProgress('0%')
  else this.handleVideoEnd()
}

function updateProgress(progress) {
  this.progress = progress
  // this.$refs.progress.style.setProperty('--progress', this.progress)
}

async function validateQuickQuestion(index) {
  const isValid = this.currentModal.correctAnswer === index
  const validityField = isValid ? 'valid' : 'invalid'
  this.currentModal[validityField] = [...this.currentModal[validityField], index]
  // const isMaxWrongAnswers = this.currentModal?.invalid.length >= MAX_WRONG_ANSWERS

  // if (!isMaxWrongAnswers) return
  // await sleep(MODAL_ANIMATION_THRESHOLD)
  // this.currentModal = {}
  // await sleep(0)
  // if (this.quickQuestionIndex === 0) {
  //   this.restartPresentation()
  // } else {
  //   this.currentModal = { ...this.quickQuestions[this.quickQuestionIndex] }
  //   this.rewindPresentation()
  // }
}

async function changeTab(tab) {
  if (tab !== this.activeTab && (tab === 'edu' || (tab === 'product' && this.eduStatus))) {
    this.activeTab = tab
    this.showSlides = true
    this.showProductModal = false
    if (tab === 'edu') this.formatQuestions(this.eduQuestion)
    else if (tab === 'product') this.formatQuestions(this.questions)
    this.formatSlides()
    this.sliderKey++
    this.hideModal()

    // Any stop moment for product video type (productType property with value "product" is video) means that the video has been viewed
    if (
      (tab === 'product' && this.compleated) ||
      (tab === 'product' &&
        ((this.slideNumber > 0 && this.productType === 'product') ||
          (this.slideNumber === this.slidesProduct.length - 1 &&
            this.productType === 'continuous')))
    ) {
      this.showOverlay('test')
      this.updateProgress('99%')
    } else {
      this.hideOverlay()
    }
    this.quickQuestionIndex = -1
    this.finalQuestionIndex = -1
    this.presentationIndex = -1
    await sleep(WAIT_THRESHOLD)
    this.setQuizHeight()
    this.showVideoOverlay = !(this.activeTab === 'product' && !this.compleated);
    this.startPresentation()
  } else if (tab !== this.activeTab && tab === 'product' && !this.eduStatus) {
    this.activeTab = tab
    this.showSlides = false
    this.showProductModal = true
    this.hideOverlay()
  }
  await sleep(WAIT_THRESHOLD)
  window.dispatchEvent(new Event('resize'))
}

function slides() {
  return this.activeTab === 'edu' ? this.slidesEdu : this.slidesProduct
}
function type() {
  return this.activeTab === 'edu' ? this.eduType : this.productType
}

function startEduTest() {
  this.hideOverlay()
  this.isModalActive = true
  this.finalQuestionIndex = 0
  this.setQuestion()
}

const $el = $('#vue-quiz-product')
if ($el) new Vue({ el: $el })
